<script setup lang="ts">
  import { ref, computed, watchEffect, watch } from "vue";
  import { useI18n } from "vue-i18n";
  import { useBarcode, useReadRfid } from "@plugins";
  import { Modal, Text, Input, IconScan, IconSearch, useNotification } from "@ui-kit";
  import { ProductNotFound, ProductMiniCard } from "@src/components";
  import {
    useItemList,
    type Product,
    useGetWTFIR,
    WTFIRInputType,
    useLazyProductsLoad,
    useLoadByExternal,
  } from "@src/api";
  import { useDebounce, useOrderCreate } from "@src/hooks";
  import { getSizeList } from "./utils";
  const { visible = false } = defineProps<{
    visible: boolean;
  }>();

  const emit = defineEmits<{
    (e: "close"): void;
    (e: "item:added"): void;
  }>();

  const search = ref<string>("");

  const { execute: getWTFIR } = useGetWTFIR();
  const { execute: getItemList, isLoading: isLoadingItemList, data: itemList, isError } = useItemList();
  const { execute: loadItem } = useLazyProductsLoad();
  const { execute: getListSeria } = useLoadByExternal();

  const debouncedSearch = useDebounce(search, 500);
  const order_create = useOrderCreate();
  const { t } = useI18n();
  const notification = useNotification();
  const barcode = useBarcode();
  const readRfid = useReadRfid();

  const handleClose = () => {
    search.value = "";
    emit("close");
  };

  const selectItem = async (item: Product) => {
    if (order_create.value.items.find((order) => order.item_id === item.item_id)) {
      notification.show({ title: t("Товар уже добавлен в список"), type: "warning" });

      return;
    }

    const sizeList = await getSizeList(item.is_virtual ? item.external_id : item.seria_guid);
    let seriaItem = item;

    if (!item.is_virtual) {
      const seriasResult = await getListSeria({ guid: [item.seria_guid] });
      if (seriasResult.data.value.items.length > 0) {
        seriaItem = seriasResult.data.value.items[0];
      } else {
        notification.show({ title: t("Цветомодель не найдена"), type: "error" });
        return;
      }
    }

    if (sizeList) {
      order_create.add({ ...seriaItem, sizeList });
      emit("item:added");
      handleClose();
    }
  };

  const handleScanData = async (data: string, input_type: WTFIRInputType) => {
    const resultWTFIR = await getWTFIR({ data, input_type });

    if (resultWTFIR.isError.value || !resultWTFIR.data.value.items || resultWTFIR.data.value.items.length === 0) {
      notification.show({ title: t("Товар не найден"), type: "error" });

      return;
    }

    const resultLoadItem = await loadItem({ item_id: resultWTFIR.data.value.items });

    if (resultLoadItem.isError.value) {
      notification.show({ title: t("Товар не найден"), type: "error" });
      return;
    }

    selectItem(resultLoadItem.data.value.items[0]);
  };

  const handleSubscribe = () => {
    barcode.subscribe((data) => handleScanData(data, WTFIRInputType.barcode));
    readRfid.subscribe((data) => handleScanData(data.value, WTFIRInputType.rfid));
  };

  const handleUnsubscribe = () => {
    barcode.unsubscribe();
    readRfid.unsubscribe();
  };

  watchEffect(() => {
    if (debouncedSearch.value.length >= 4) {
      getItemList({ article: debouncedSearch.value });
    }
  });

  watch(
    () => visible,
    () => {
      if (visible) {
        handleSubscribe();
      } else {
        handleUnsubscribe();
      }
    },
  );

  const visibleNotFound = computed(
    () => search.value.length > 0 && (isError.value || itemList.value?.items.length === 0),
  );
  const visibleItems = computed(() => search.value.length > 3 && itemList.value && itemList.value?.items.length > 0);
</script>

<template>
  <Modal v-if="visible" close @onClose="handleClose" data-testid="order-create-add-product-modal">
    <template #header>
      <div class="order-create-modal__header">
        <Text font="body-bold-18">{{ $t("Добавление товара") }}</Text>
        <Text font="body-regular-14" color="secondary">{{ $t("Отсканируйте товар или введите артикул") }}</Text>
      </div>
    </template>
    <div class="order-create-modal__content">
      <Input
        v-model="search"
        placeholder="0000–000–000"
        :iconAfter="IconSearch"
        size="l"
        inputMode="numeric"
        v-maska="'####-###-###'"
        data-testid="order-create-modal__input"
      />
      <ProductNotFound v-if="visibleNotFound" />
      <div
        v-else-if="visibleItems"
        class="order-create-modal__items"
        :class="{ 'loading': isLoadingItemList }"
        data-testid="order-create-modal__items"
      >
        <template v-for="item in itemList?.items" :key="item.item_id">
          <ProductMiniCard
            v-if="Boolean(item.parent_guid)"
            :image="item.image_url"
            :name="item.title"
            :article="item.article"
            :color="item.color"
            @click="selectItem(item)"
            data-testid="order-create-modal__item"
          />
        </template>
      </div>
      <template v-else>
        <div class="order-create-modal__separator">
          <hr />
          <Text font="body-regular-12" color="tertiary">{{ $t("или") }}</Text>
          <hr />
        </div>
        <div class="order-create-modal__scan">
          <IconScan />
          <Text font="body-regular-12" color="secondary">{{ $t("Нажмите на курок гана") }}</Text>
        </div>
      </template>
    </div>
  </Modal>
</template>

<style scoped>
  .order-create-modal__header {
    display: flex;
    flex-direction: column;
    align-items: center;

    & :deep(.text:first-of-type) {
      padding: 3px 0;
    }
  }

  .order-create-modal__content {
    display: flex;
    flex-direction: column;
    gap: 12px;

    & :deep(.input) {
      width: 100%;
    }
  }

  .order-create-modal__separator {
    display: flex;
    align-items: center;
    width: 100%;
    gap: 12px;
    margin: 16px 0;

    hr {
      flex: 1;
      border: 0;
      border-bottom: 1px solid transparent;
      border-image: repeating-linear-gradient(to right, var(--sf-base-border-primary) 0 6px, transparent 6px 12px) 1;
    }
  }

  .order-create-modal__scan {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  .order-create-modal__items {
    display: flex;
    flex-direction: column;
    gap: 8px;

    & :deep(.item) {
      padding: 12px;
      border-radius: 16px;
      background-color: var(--sf-base-background-white);
    }

    &.loading {
      opacity: 0.4;
    }
  }
</style>
